import { Box, Chip, Stack } from "@mui/material";
import React, { FC } from "react";
import ReactJson from "react-json-view";
import { CellProps, Column } from "react-table";

import { getLogs } from "api/logs";
import { logsKeys } from "api/logs/queries";
import { LogsType } from "api/logs/types";
import { QueryParamsType } from "api/utils";
import Table from "components/Table";
import { dateTimeFormatter } from "components/Table/formatters/dateTimeFormatter";
import useRemoteTableLogic from "components/Table/useRemoteTableLogic";

import { useWebhookLogsFilter } from "./useWebhookLogsFilter";

function parseJsonSafe(value: string | null | undefined): object | null {
  if (value == null || value === "") return null;
  try {
    const parsed = JSON.parse(value);
    if (typeof parsed === "object" && parsed !== null) return parsed;
    return { value: parsed };
  } catch {
    return null;
  }
}

const JsonViewerCell: FC<{
  value: string | null | undefined;
  fallback?: React.ReactNode;
}> = ({ value, fallback }) => {
  const parsed = parseJsonSafe(value);
  if (parsed === null) {
    return (
      <Box sx={{ fontFamily: "monospace", fontSize: "0.8125rem" }}>
        {fallback ?? (value ? <pre style={{ margin: 0 }}>{value}</pre> : "—")}
      </Box>
    );
  }
  return (
    <Box
      sx={{
        "& .react-json-view": { fontSize: "0.8125rem" },
        maxHeight: 320,
        overflow: "auto",
      }}
    >
      <ReactJson
        src={parsed}
        name={false}
        theme="rjv-default"
        collapsed={2}
        collapseStringsAfterLength={80}
        enableClipboard
        displayDataTypes={false}
        displayObjectSize
        style={{ background: "transparent" }}
      />
    </Box>
  );
};

interface WebhookLogsTableProps {
  filters?: QueryParamsType<LogsType>;
}

const WebhookLogsTable: FC<WebhookLogsTableProps> = ({
  filters: filtersFromProps,
}) => {
  const filterHook = useWebhookLogsFilter();
  const filters = filtersFromProps ?? filterHook.filters;

  const { data, status, sortOptions, paginationOptions } = useRemoteTableLogic(
    logsKeys.list(),
    getLogs,
    filters,
  );

  const columns: Column<LogsType>[] = [
    {
      accessor: "url",
      Header: "URL",
    },
    {
      accessor: "method",
      Header: "Method",
    },
    {
      accessor: "body",
      Header: "Body",
      disableSortBy: true,
      Cell: ({ value }: CellProps<LogsType>) => (
        <JsonViewerCell value={value} />
      ),
    },
    {
      accessor: "statusCode",
      Header: "Status Code",
      Cell: ({ value }: CellProps<LogsType>) =>
        value ? (
          <Chip label={value} color={value < 400 ? "success" : "error"}></Chip>
        ) : (
          <></>
        ),
    },
    {
      accessor: "response",
      Header: "Response",
      Cell: ({ value, row }: CellProps<LogsType>) => (
        <JsonViewerCell
          value={value}
          fallback={
            row.original.statusMessage ? (
              <pre style={{ margin: 0 }}>{row.original.statusMessage}</pre>
            ) : undefined
          }
        />
      ),
    },
    {
      accessor: "createdAt",
      Header: "Date",
      Cell: ({ value }: CellProps<LogsType>) => <>{dateTimeFormatter(value)}</>,
    },
  ];

  return (
    <Stack spacing={2} flex={1} height="100%" overflow="hidden">
      <Table
        data={data}
        columns={columns}
        sort={sortOptions}
        pagination={paginationOptions}
        status={status}
        filters={filters}
        initialState={{
          sortBy: [
            {
              id: "createdAt",
              desc: true,
            },
          ],
        }}
      />
    </Stack>
  );
};

export default WebhookLogsTable;
